/**
 * 1.25 — the till opens from the device's product copy (R07, the product half).
 *
 * A reload used to read the whole catalogue: 32 pages and the branch's stock,
 * ~6 s of a 30 000-product chain's 6.3 s reload. The device already keeps a
 * copy (lib/product-cache.ts); migration 114 already answers „what changed
 * since" — and `products.updated_at` moves with every change, the stock fold
 * of a sale or a transfer included. These rules decide when the copy plus one
 * delta IS the catalogue, and anything they doubt goes back to the full read.
 *
 * Identity is taken from this boot's fresh reads, never the persisted state,
 * and never the device clock (the watermark is the server's time).
 */
import type { Product } from './mock-data'
import { MAX_CHANGED, mergeChanged } from './product-sync'
import { matchingProductCache } from './product-cache'
import { pickMembership, type MembershipRow } from './support'

export type CacheEntryV2 = {
  v: 2
  /** 158 (1.40): written by a client that keeps `extra_barcodes`. A 1.39
   *  device drops the field from the deltas it reads and still stamps a newer
   *  watermark, so a copy without this mark is never warm (see warmCopy).
   *  Optional so the older entries keep their shape; `v` stays 2. */
  codes?: 1
  /** 187 (1.62): written by a client that keeps `fits` and `oem_codes`. A 1.61
   *  device drops both from its deltas, and adding the columns touched no row,
   *  so no later delta brings them — a copy without this mark is never warm
   *  (the `codes` reasoning, 158). */
  fits?: 1
  /** 188 (1.63): written by a client that keeps `club_price`. A 1.62 device drops it from its deltas — the
   *  `fits` reasoning again: a copy without this mark is never warm. */
  club?: 1
  /** 196 (1.68): written by a client that keeps `system_kind`. A 1.67 device drops it from its deltas, so the
   *  shop's service product would sit in its copy as an ordinary ₾0 product, on the till's grid — the `fits` reasoning
   *  again: a copy without this mark is never warm. */
  svc?: 1
  userId: string
  orgId: string
  /** The branch whose stock `quantity` holds; null = the org-wide figure. */
  branchId: string | null
  /** Cost visible when read (owner | accountant); null = unknown. Never
   *  inferred from the rows: a hidden purchase_price is mapped to 0. */
  books: boolean | null
  /** The server time the data is current to. Older than the data is safe
   *  (the next delta overlaps); newer never happens (see the store). */
  watermark: string | null
  /** Changed on this device with no server change behind it (an offline
   *  sale's decrement): never warm until a full read. */
  local: boolean
  /** Informational only — the device clock is never compared. */
  savedAt: string
  products: Product[]
}

export type CacheSnapshot = Omit<CacheEntryV2, 'v' | 'codes' | 'fits' | 'club' | 'svc' | 'savedAt'>
export type ProductsMeta = { watermark: string | null; lens: string | null; local: boolean }

export function isWatermark(w: unknown): w is string {
  return typeof w === 'string' && Number.isFinite(Date.parse(w))
}

/** The same answer as auth_can_read_books(); a role we do not know is unknown. */
export function booksFor(role: string | null | undefined): boolean | null {
  if (role === 'owner' || role === 'accountant') return true
  if (role === 'cashier') return false
  return null
}

export function warmCopy(
  entry: unknown,
  fresh: { userId: string | null | undefined; orgId: string | null | undefined; branchId: string | null; books: boolean | null },
): { products: Product[]; watermark: string } | null {
  if (!entry || typeof entry !== 'object') return null
  const e = entry as Partial<CacheEntryV2>
  if (e.v !== 2 || e.local !== false) return null
  if (!fresh.userId || !fresh.orgId || fresh.books === null) return null
  if (e.userId !== fresh.userId || e.orgId !== fresh.orgId) return null
  if ((e.branchId ?? null) !== fresh.branchId) return null
  if (e.books !== fresh.books) return null
  if (!isWatermark(e.watermark)) return null
  if (!Array.isArray(e.products) || e.products.length === 0) return null
  // 153 (Codex's review): a copy written before the website switch has neither
  // mark, and adding the columns touched no row, so no delta would bring them —
  // one full read fills them. (Offline, such a copy is still shown: the product
  // dialog writes the marks only when its switch is touched.)
  if (!('online_hidden' in (e.products[0] as object))) return null
  // 158 (Codex's #3, adopted after the 1.40 final review): a copy written by a
  // 1.39 client has no `extra_barcodes` — its mapper drops the field from every
  // delta it reads, yet it stamps a newer watermark, so no later delta would
  // bring the codes back — one full read fills them (once per device, on its
  // first 1.40 boot). The mark is on the entry, not on a product: a product
  // built locally without the key must not refuse the copy on every boot. A
  // rollback to 1.39 writes unmarked entries again, and the next 1.40 boot
  // refuses them. (Offline, such a copy is still shown — there is no choice —
  // with a meta that never lets its re-save be warm: see offlineCopy.)
  if (e.codes !== 1) return null
  // 187 (1.62) — the same for the fitment fields: one full read per device, on its first 1.62 boot
  if (e.fits !== 1) return null
  // 188 (1.63) — and for the club price
  if (e.club !== 1) return null
  // 196 (1.68) — and for the service product's mark
  if (e.svc !== 1) return null
  return { products: e.products, watermark: e.watermark }
}

/**
 * The copy completed by the server's answer, or null → the full read. The
 * rows must already be mapped the way refreshProducts maps them. A deleted
 * product leaves its id in the copy, so merged − total is the deleted count.
 */
export function acceptDelta(
  copy: { products: Product[]; watermark: string },
  ans: { now?: unknown; total?: unknown; rows?: Product[] | null } | null,
): { products: Product[]; watermark: string } | null {
  if (!ans || !Array.isArray(ans.rows) || ans.rows.length > MAX_CHANGED) return null
  if (!isWatermark(ans.now) || Date.parse(copy.watermark) > Date.parse(ans.now)) return null
  const merged = mergeChanged(copy.products, ans.rows)
  if (merged.length !== Number(ans.total)) return null
  return { products: merged, watermark: ans.now }
}

/** What an offline boot may show: v1 by user + org (as before, marked local),
 *  v2 also by the persisted active branch when the copy has a lens. */
export function offlineCopy(
  entry: unknown,
  persisted: { userId: string | null | undefined; orgId: string | null | undefined; activeBranchId: string | null },
): { products: Product[]; meta: ProductsMeta } | null {
  const products = matchingProductCache(entry, persisted.userId, persisted.orgId)
  if (!products) return null
  const e = entry as Partial<CacheEntryV2>
  if (e.v !== 2) return { products, meta: { watermark: null, lens: null, local: true } }
  const lens = e.branchId ?? null
  if (lens !== null && lens !== persisted.activeBranchId) return null
  // 158 (Codex's build review #1): a copy a 1.39 client wrote is shown, but the
  // store saves the list again through productCacheEntry, which stamps
  // `codes: 1` — kept with its watermark, the next online boot would trust it
  // and the codes it never had would stay „not found" on this till. No
  // watermark and `local` keep it out of warmCopy; the reconnect refresh reads
  // everything (refreshPath: no watermark → full).
  if (e.codes !== 1 || e.fits !== 1 || e.club !== 1 || e.svc !== 1) return { products, meta: { watermark: null, lens, local: true } }
  return { products, meta: { watermark: isWatermark(e.watermark) ? e.watermark : null, lens, local: e.local !== false } }
}

/** The list and its meta in one snapshot — what the store saves. */
export function cacheSnapshot(
  s: { cachedUserId: string | null; orgId: string | null | undefined; userRole: string | null; products: Product[] },
  meta: ProductsMeta,
): CacheSnapshot | null {
  if (!s.cachedUserId || !s.orgId || s.products.length === 0) return null
  return {
    userId: s.cachedUserId, orgId: s.orgId, branchId: meta.lens, books: booksFor(s.userRole),
    watermark: meta.watermark, local: meta.local, products: s.products,
  }
}

export type FreshIdentity = { userId: string; orgId: string; branchId: string | null; books: boolean | null }

/**
 * This boot's identity for the copy — only when the device and the server
 * cannot disagree about it (Codex's 1.25 review). A temporary (support)
 * membership is in force until the SERVER's now(); the device judges its
 * expiry by its own clock (pickMembership), so near the end of a grant the
 * two can pick different shops. With only permanent rows the choice is the
 * oldest one on both sides, no clock involved. Anything else → null → the
 * full read, which the server scopes itself.
 */
export function freshIdentity(
  userId: string | null | undefined,
  rows: MembershipRow[],
  lens: string | null,
): FreshIdentity | null {
  if (!userId || rows.length === 0 || rows.some((r) => r.expires_at)) return null
  const m = pickMembership(rows)
  if (!m) return null
  return { userId, orgId: m.org_id, branchId: lens, books: booksFor(m.role) }
}

/**
 * Which read a product refresh makes (Codex's 1.25 review). A delta's rows
 * are lensed through the branch active NOW; merged into a list that holds
 * another lens — a full read whose stock read failed (org-wide figures), or
 * a branch switch whose hydrate has not landed — it mixes two lenses in one
 * list, and the copy would keep that mix. Then everything is read again.
 */
export function refreshPath(o: {
  full?: boolean
  watermark: string | null
  count: number
  listLens: string | null
  lens: string | null
}): 'delta' | 'full' {
  if (o.full || !o.watermark || o.count === 0) return 'full'
  return o.listLens === o.lens ? 'delta' : 'full'
}
